import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { StatusBadge } from '../widgets/StatusBadge';
import {
  UserPlus,
  FileText,
  CreditCard,
  MessageSquare,
  ChevronRight,
  Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Format relative time
const formatTime = (date) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch {
    return 'Recently';
  }
};

// Recent Students Feed
const RecentStudentsFeed = ({ students, loading }) => {
  const navigate = useNavigate();

  if (loading) return <FeedSkeleton title="Recent Students" icon={UserPlus} />;

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-100">
            <UserPlus className="w-4 h-4 text-blue-500" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Recent Students</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/students')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-2">
        {!students || students.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No recent students</p>
        ) : (
          students.map((student, index) => (
            <div
              key={student.studentId || index}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer group"
              onClick={() => navigate(`/admin/students/${student.studentId}`)}
            >
              <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {student.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{student.name}</p>
                <p className="text-xs text-gray-500 truncate">{student.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {formatTime(student.createdAt)}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Recent Applications Feed
const RecentApplicationsFeed = ({ applications, loading }) => {
  const navigate = useNavigate();

  if (loading) return <FeedSkeleton title="Recent Applications" icon={FileText} />;

  const formatServiceType = (type) => {
    return type?.replace(/_/g, ' ')?.toLowerCase()?.replace(/\b\w/g, c => c.toUpperCase()) || 'Service';
  };

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-indigo-50 border border-indigo-100">
            <FileText className="w-4 h-4 text-indigo-500" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Recent Applications</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/service-requests')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-2">
        {!applications || applications.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No recent applications</p>
        ) : (
          applications.map((app, index) => (
            <div
              key={app.requestId || index}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer group"
              onClick={() => navigate(`/admin/service-requests/${app.requestId}`)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {formatServiceType(app.serviceType)}
                </p>
                <p className="text-xs text-gray-500 truncate">{app.studentName}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={app.status} size="xs" />
                <ChevronRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Recent Payments Feed
const RecentPaymentsFeed = ({ payments, loading }) => {
  const navigate = useNavigate();

  if (loading) return <FeedSkeleton title="Recent Payments" icon={CreditCard} />;

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
            <CreditCard className="w-4 h-4 text-emerald-500" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Recent Payments</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/payments')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-2">
        {!payments || payments.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No recent payments</p>
        ) : (
          payments.map((payment, index) => (
            <div
              key={payment.paymentId || index}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors group"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                payment.status === 'completed' ? 'bg-emerald-50 border border-emerald-100' :
                payment.status === 'failed' ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'
              }`}>
                <CreditCard className={`w-4 h-4 ${
                  payment.status === 'completed' ? 'text-emerald-600' :
                  payment.status === 'failed' ? 'text-red-600' : 'text-amber-600'
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  ${payment.amount?.toLocaleString()} {payment.currency}
                </p>
                <p className="text-xs text-gray-500 truncate">{payment.studentName}</p>
              </div>
              <StatusBadge status={payment.status} size="xs" />
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Recent Messages Feed
const RecentMessagesFeed = ({ messages, loading }) => {
  const navigate = useNavigate();

  if (loading) return <FeedSkeleton title="Recent Messages" icon={MessageSquare} />;

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-purple-50 border border-purple-100">
            <MessageSquare className="w-4 h-4 text-purple-500" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Recent Messages</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/messages')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-2">
        {!messages || messages.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No recent messages</p>
        ) : (
          messages.map((message, index) => (
            <div
              key={message.messageId || index}
              className="flex items-start gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer group"
            >
              <div className="w-9 h-9 bg-purple-50 border border-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-purple-600">
                  {message.senderName?.charAt(0) || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{message.senderName}</p>
                  {!message.isRead && (
                    <span className="w-2 h-2 bg-[#F2575F] rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{message.content}</p>
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                {formatTime(message.createdAt)}
              </span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Feed Skeleton
const FeedSkeleton = ({ title, icon: Icon }) => (
  <Card className="p-5 bg-white border-stone-200/80">
    <div className="flex items-center gap-2 mb-4">
      <div className="p-1.5 rounded-lg bg-stone-50 border border-stone-100">
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <h3 className="font-semibold text-gray-900 font-outfit">{title}</h3>
    </div>
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2">
          <div className="w-9 h-9 bg-stone-100 rounded-full animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-stone-100 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-stone-100 rounded animate-pulse w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </Card>
);

// Main Component
export function RecentActivityFeeds({ recentActivity, loading }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
    >
      <RecentStudentsFeed students={recentActivity?.students} loading={loading} />
      <RecentApplicationsFeed applications={recentActivity?.applications} loading={loading} />
      <RecentPaymentsFeed payments={recentActivity?.payments} loading={loading} />
      <RecentMessagesFeed messages={recentActivity?.messages} loading={loading} />
    </motion.div>
  );
}

export default RecentActivityFeeds;
